import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { epochDayOf, todayPlainDate } from '../../../core/services/plain-date';
import { extendRange, initialRange, type DayRange } from '../model/range';
import { buildRows } from '../model/row-model';
import { createTimeScale } from '../model/time-scale';
import {
  DEFAULT_WORKING_WEEK,
  type GanttConfig,
  type GanttItem,
  type Row,
  type TimeScale,
} from '../types';

export type GanttState = {
  rows: Row[];
  scale: TimeScale;
  range: DayRange;
  todayDay: number;
  workingWeek: readonly number[];
  isCollapsed: (id: string) => boolean;
  toggleCollapsed: (id: string) => void;
  extend: (side: 'start' | 'end') => void;
  /**
   * O(1) lookup for the row that stands in for `id` — itself when visible, otherwise the
   * collapsed ancestor rendered in its place. See `RowModel.ancestorRowOf`.
   */
  ancestorRowOf: (id: string) => Row | undefined;
};

const EMPTY: ReadonlySet<string> = new Set();

/**
 * What a range re-seed must react to, reduced to a primitive a `useEffect` dependency array can
 * compare BY VALUE. Object identity is useless as a gate here — both routes rebuild `items` on
 * every MobX tick, so `seeded` is a fresh object on every render whether or not the window it
 * describes has moved a single day.
 *
 * I2 — WHY THIS IS THE SEEDED RANGE AND NOT THE RAW `min:max` EXTENT. It used to be the extent,
 * and that gate fired on ANY change to it, however small — including one the user themselves had
 * just caused by dragging the earliest or latest bar by one day. `initialRange` quantises to whole
 * months with six months of padding on each side, so a one-day drag almost never changes the
 * SEEDED range at all; the extent-keyed gate threw the user's accumulated `extend()` scrolling
 * away anyway, because it reset the override rather than recomputing it. Measured: two
 * `extend('start')` calls gained 365 days, and a one-day drag of the only bar discarded all of
 * them — 17,520px of content at 48px/day, with `gantt-chart.tsx`'s scroll compensation only
 * covering growth, so the viewport jumped as well. Keying on the seeded range's own VALUE means
 * the reset happens exactly when the window the data needs has genuinely moved.
 *
 * `seeded` derives from `model.rows` (the CURRENTLY collapsed view), not a second uncollapsed walk
 * of the whole tree. That is safe, not merely convenient: `row-model.ts`'s `resolveSpans` computes
 * every item's span in one pass over the FULL tree before collapse is even consulted, so a
 * collapsed group's own row already carries its entire hidden subtree's span. Min/max over the
 * visible rows therefore always equals min/max over the whole tree — collapse only changes
 * `rows.length` (which entries exist), never `min`/`max` (how far they reach). Pinned by
 * `use-gantt-state.test.tsx`'s "re-seeds the range when the item that defines the extent is
 * inside a collapsed group".
 */
const rangeSignatureOf = (range: DayRange): string => `${range.startDay}:${range.endDay}`;

/**
 * The chart's single owner of collapse, zoom and window state.
 *
 * Collapse has exactly one owner here — optionally the caller, via `collapsed` +
 * `onCollapsedChange`. The old module had two (radix's own `useState(defaultOpen)` and the
 * provider's `collapsibleStates`, which the chart filtered on) and they could disagree, which
 * hid rows in the chart that the sidebar still showed.
 */
export const useGanttState = (items: GanttItem[], config: GanttConfig = {}): GanttState => {
  const { collapsed: controlled, defaultCollapsed, onCollapsedChange } = config;

  const [uncontrolled, setUncontrolled] = useState<ReadonlySet<string>>(defaultCollapsed ?? EMPTY);
  const collapsed = controlled ?? uncontrolled;

  // Ids that have already had their `defaultCollapsed` status applied — from the `useState`
  // initializer above, or from the collapse-reapplication effect below. Once an id is here, that
  // effect leaves it alone forever, even if the caller's `defaultCollapsed` still lists it: that
  // is what lets a user-expanded group survive later item changes instead of snapping shut again.
  //
  // This exists because `defaultCollapsed` is not actually static. The space route computes it as
  // `useMemo(() => initialCollapsedFor(items), [items])` (`multi-projects-calendar.page.tsx:241`)
  // — a NEW set every time `items` changes — but the `useState` above only ever reads its
  // initializer once. Without this, a project arriving after mount never has its id added to
  // `uncontrolled` at all, and renders permanently expanded.
  //
  // M6: built LAZILY. `useRef(new Set(...))` evaluates its argument on every render and throws
  // the result away on all but the first — cosmetic at this size, but it reads as though the ref
  // tracks `defaultCollapsed`, which is the opposite of what it is for.
  const seededIdsRef = useRef<Set<string>>(null);
  if (!seededIdsRef.current) seededIdsRef.current = new Set(defaultCollapsed ?? EMPTY);

  const todayDay = useMemo(() => epochDayOf(config.today ?? todayPlainDate()), [config.today]);

  const model = useMemo(() => buildRows(items, collapsed), [items, collapsed]);

  const scale = useMemo(() => createTimeScale(config.zoom ?? 'day'), [config.zoom]);

  const rangeOptions = config.range;
  const [rangeOverride, setRangeOverride] = useState<DayRange | null>(null);

  const seeded = useMemo(
    () => initialRange(model.rows, todayDay, rangeOptions),
    // Deliberately NOT keyed on `collapsed`, so collapsing a stage cannot narrow the window
    // under the user mid-session. That is safe rather than stale: a collapsed group's span is
    // the union of the descendants it is hiding, so the min/max over the visible rows is the
    // same whatever is collapsed — collapse changes which rows exist, never how far they reach.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, todayDay, rangeOptions],
  );

  // Read by `extend` below through a ref, not a closure, so a caller holding an old `extend`
  // reference (e.g. an effect with a deliberately trimmed dependency list, as in
  // `gantt-chart.tsx`'s extension effect) still extends from the CURRENT seed rather than
  // whichever one was in scope when that stale closure was created.
  const seededRef = useRef(seeded);
  seededRef.current = seeded;

  const rangeSignature = rangeSignatureOf(seeded);

  // The range re-seed gate: `rangeSignature` is a primitive, so this only re-runs when the SEEDED
  // WINDOW actually moves — never on the identity-only churn both routes produce every MobX tick,
  // never on a collapse toggle (which changes `model.rows` but not its extent), and — I2 — never
  // on an extent change that quantises away to the same six-month-padded window, which is what
  // every ordinary drag of the extreme bar produces. Reset to `null` rather than to `seeded`
  // directly so `range` always reads through the same `??` below — including on mount, where this
  // is a harmless no-op.
  useEffect(() => {
    setRangeOverride(null);
  }, [rangeSignature]);

  // The collapse-reapplication gate is deliberately NOT `rangeSignature` (nor any other reduced
  // extent/count signature): what this needs to know is "did a new id enter `defaultCollapsed`",
  // and an extent-or-count signature is a poor proxy for that — a same-count, same-extent swap of
  // one group's items for another's would leave such a signature unchanged and silently skip
  // reapplication for the new id. `defaultCollapsed` itself is the exact value being diffed, so
  // it is what gates the diff. The diff is cheap regardless of how often it runs — bounded by
  // `defaultCollapsed`'s own size, not a walk of the whole row tree — so there is no need to
  // compress it into a memoized primitive first the way the range signature is.
  useEffect(() => {
    if (controlled) return;

    const currentDefaults = defaultCollapsed ?? EMPTY;
    const newlySeeded: string[] = [];
    currentDefaults.forEach((id) => {
      if (!seededIdsRef.current.has(id)) newlySeeded.push(id);
    });

    if (newlySeeded.length === 0) return;

    newlySeeded.forEach((id) => seededIdsRef.current.add(id));
    setUncontrolled((current) => {
      const next = new Set(current);
      newlySeeded.forEach((id) => next.add(id));

      return next;
    });
  }, [defaultCollapsed, controlled]);

  const range = rangeOverride ?? seeded;

  const toggleCollapsed = useCallback(
    (id: string) => {
      const next = new Set(collapsed);
      if (next.has(id)) next.delete(id);
      else next.add(id);

      if (!controlled) setUncontrolled(next);
      onCollapsedChange?.(next);
    },
    [collapsed, controlled, onCollapsedChange],
  );

  const extend = useCallback(
    (side: 'start' | 'end') => {
      setRangeOverride((current) => extendRange(current ?? seededRef.current, side, rangeOptions));
    },
    [rangeOptions],
  );

  const isCollapsed = useCallback((id: string) => collapsed.has(id), [collapsed]);

  return {
    rows: model.rows,
    scale,
    range,
    todayDay,
    workingWeek: config.workingWeek ?? DEFAULT_WORKING_WEEK,
    isCollapsed,
    toggleCollapsed,
    extend,
    ancestorRowOf: model.ancestorRowOf,
  };
};
